// react
import { useCallback, useMemo, useState } from 'react';

// mui
import { Box, Button, Stack, TextField } from '@mui/material';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import {
  Checkbox,
  CheckboxSize,
  CheckboxVariant,
} from '@/components/atoms/Checkbox/Checkbox';
import { Option } from '@/components/atoms/Select/Select';

export interface MultiSelectBoxProps extends FieldProps<string> {
  className?: string;
  maxColumns?: number;
  size?: CheckboxSize;
  options: Option[];
  isCustom?: boolean;
  variant?: CheckboxVariant;
}

const parseValueArray = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/**
 * MultiSelectBox lets the user pick multiple values from a list of
 * box/card-style checkboxes laid out in a grid. When isCustom is true the
 * user can also add free-text values.
 */
export const MultiSelectBox = ({
  className,
  id,
  ariaLabel,
  label,
  description,
  tooltip,
  isRequired,
  options,
  value,
  maxColumns,
  size,
  onChange,
  isCustom,
  validate,
  variant = 'box',
  isDisabled = false,
  isHideTitle = false,
}: MultiSelectBoxProps) => {
  const [customValue, setCustomValue] = useState('');
  const [isAddAnother, setIsAddAnother] = useState(false);

  const valueArray = useMemo(() => parseValueArray(value), [value]);

  const customValues = useMemo(
    () =>
      valueArray.filter((v) => !options.find((option) => option.value === v)),
    [valueArray, options],
  );

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [options],
  );

  const toggle = useCallback(
    (v: string) => {
      const idx = valueArray.indexOf(v);
      if (idx === -1) onChange?.(JSON.stringify([...valueArray, v]));
      else {
        const next = [...valueArray];
        next.splice(idx, 1);
        onChange?.(JSON.stringify(next));
      }
    },
    [valueArray, onChange],
  );

  const handleSaveCustom = useCallback(() => {
    const trimmed = customValue.trim();
    if (trimmed && !valueArray.includes(trimmed)) {
      onChange?.(JSON.stringify([...valueArray, trimmed]));
    }
    setIsAddAnother(false);
    setCustomValue('');
  }, [customValue, valueArray, onChange]);

  const cols = maxColumns ?? 2;

  return (
    <BaseField
      id={id}
      label={label}
      ariaLabel={ariaLabel}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Stack className={className} spacing={2}>
        {sortedOptions.length + customValues.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: 2,

              gridTemplateColumns: {
                xs: '1fr',
                sm: `repeat(${cols}, minmax(0, 1fr))`,
              },
            }}
          >
            {sortedOptions.map((option) => (
              <Checkbox
                key={option.value}
                id={option.value}
                label={option.label}
                variant={variant}
                size={size ?? 'sm'}
                value={valueArray.includes(option.value)}
                onChange={() => toggle(option.value)}
                isDisabled={isDisabled}
                icon={option.icon}
                iconFill={option.iconFill}
              />
            ))}
            {customValues.map((v) => (
              <Checkbox
                key={v}
                id={`${id}-${v}`}
                label={v}
                variant={variant}
                size={size ?? 'sm'}
                value={valueArray.includes(v)}
                onChange={() => toggle(v)}
                isDisabled={isDisabled}
              />
            ))}
          </Box>
        )}
        {isCustom && (
          <Stack
            spacing={2}
            component='form'
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              handleSaveCustom();
            }}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            {!isAddAnother ? (
              <Button
                variant='outlined'
                onClick={() => setIsAddAnother(true)}
                disabled={isDisabled}
              >
                Add another
              </Button>
            ) : (
              <>
                <TextField
                  id='addAnotherInput'
                  placeholder='Something else'
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  disabled={isDisabled}
                  autoFocus
                  size='small'
                  slotProps={{
                    htmlInput: { 'aria-label': 'something else' },
                  }}
                />
                <Stack
                  direction='row'
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant='outlined'
                    onClick={handleSaveCustom}
                    disabled={
                      isDisabled || (validate && !validate(customValue))
                    }
                  >
                    Save
                  </Button>
                  <Button
                    variant='text'
                    onClick={() => {
                      setIsAddAnother(false);
                      setCustomValue('');
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </BaseField>
  );
};

MultiSelectBox.displayName = 'MultiSelectBox';

export default MultiSelectBox;
